import axios from 'axios';
import keycloak, { keycloakEnabled } from '../keycloak';
import { ENDPOINTS } from '../config/endpoints';
import { OFFER_VERSION, CONSENT_VERSION } from '../config/legalVersions';
import { SUPPORT_EMAIL } from '../config/branding';

const API_BASE_URL = ENDPOINTS.auth;
const LOGIN_NOTICE_KEY = 'newton:login-notice';

// Событие «имя в профиле изменилось», detail — новое имя.
export const DISPLAY_NAME_EVENT = 'newton:display-name';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

export const authService = {
  async requestToken(email) {
    try {
      const response = await api.post('/request-token', { email });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 403) {
        throw new Error('Домен электронной почты не разрешен.');
      }
      throw new Error('Ошибка при запросе токена. Попробуйте позже.');
    }
  },

  async register(email) {
    try {
      const response = await api.post('/register', { email });
      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 403) {
          // Код нужен вызывающему: 403 по домену означает «это внешний адрес»,
          // и AuthPage переводит человека в регистрацию для внешних клиентов.
          const denied = new Error('Домен электронной почты не разрешён.');
          denied.code = 'domain_not_allowed';
          throw denied;
        }
        if (error.response.status === 409) {
          throw new Error('Аккаунт с таким email уже существует. Войдите.');
        }
        if (error.response.status === 503) {
          throw new Error('Регистрация временно недоступна.');
        }
      }
      throw new Error('Ошибка при регистрации. Попробуйте позже.');
    }
  },

  async signupEnabled() {
    try {
      const response = await api.get('/signup/enabled');
      return !!response.data.enabled;
    } catch (error) {
      // Ошибка чтения трактуется как «выключено»: мёртвой ссылки быть не должно.
      return false;
    }
  },

  async signup(email) {
    try {
      const response = await api.post('/signup', { email });
      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 409) {
          throw new Error('Корпоративный адрес — войдите через вход для сотрудников.');
        }
        if (error.response.status === 429) {
          // Текст сервера точнее: он называет, сколько минут ждать и чего
          // именно слишком много — кодов на адрес или запросов с сети (тест 25.09).
          const detail = error.response.data?.detail;
          throw new Error(typeof detail === 'string' ? detail : 'Слишком много запросов кода. Попробуйте позже.');
        }
        if (error.response.status === 503) {
          // Сервер называет причину — например, что письмо с кодом не ушло.
          const detail = error.response.data?.detail;
          throw new Error(typeof detail === 'string' ? detail : 'Регистрация временно недоступна.');
        }
      }
      throw new Error('Ошибка регистрации. Попробуйте позже.');
    }
  },

  async requestPasswordReset(email) {
    try {
      const response = await api.post('/password-reset', { email });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 404) {
        // Ради этой ветки ручка и заведена: Keycloak на незнакомый адрес молчит
        // и показывает то же «письмо отправлено», а человек ждёт письмо, которого
        // не будет. Признак has_legacy_account решает, куда его вести дальше.
        const missing = new Error(detail?.message || 'Учётной записи с таким адресом нет.');
        missing.code = 'user_not_found';
        missing.hasLegacyAccount = Boolean(detail?.has_legacy_account);
        throw missing;
      }
      if (status === 403) {
        const denied = new Error('Смена пароля есть только у корпоративной почты.');
        denied.code = 'domain_not_allowed';
        throw denied;
      }
      // 429 приходит с готовым текстом: он называет, какой именно потолок задет —
      // письма на этот адрес или запросы с этой сети.
      if (status === 429) {
        throw new Error(typeof detail === 'string'
          ? detail
          : 'Слишком много запросов. Попробуйте позже.');
      }
      if (status === 503) throw new Error('Смена пароля временно недоступна.');
      throw new Error('Письмо не ушло. Попробуйте позже.');
    }
  },

  async confirmSignup(email, code, ref = null) {
    try {
      // ref — токен публичной ссылки (lib/referralRef.js): по нему bit-auth привяжет
      // новую учётку к поделившемуся. Без ref тело как раньше: bit-auth отвергает
      // пустую строку 422-й, поэтому ключ добавляем только при непустом токене.
      // legal — редакции, с которыми человек согласился отметками на экране: bit-auth
      // пишет акцепт после проверки кода (журнал legal_acceptances).
      const legal = { offer: OFFER_VERSION, consent: CONSENT_VERSION };
      const body = ref ? { email, code, ref, legal } : { email, code, legal };
      const response = await api.post('/signup/confirm', body);
      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          // Остаток попыток приходит отдельным полем: без него человек видит
          // «Неверный код» пять раз подряд и на шестой молча получает отказ.
          const wrong = new Error('Неверный код.');
          wrong.attemptsLeft = error.response.data?.attempts_left;
          throw wrong;
        }
        if (error.response.status === 410) throw new Error('Код истёк, запросите новый');
        if (error.response.status === 429) throw new Error('Слишком много попыток, запросите новый код');
        if (error.response.status === 403) {
          // Заведение новых учётных записей выключено тумблером в админке;
          // существующий клиент при этом входит как обычно.
          throw new Error(error.response.data?.detail
            || 'Регистрация новых учётных записей временно закрыта.');
        }
      }
      throw new Error('Ошибка подтверждения. Попробуйте позже.');
    }
  },

  // Вход по одноразовой ссылке из приглашения в организацию. Токен — в теле,
  // не в адресе запроса: адрес оседает в логах прокси, а ссылка живая до гашения.
  async redeemLoginLink(token, consent = false, ref = null) {
    try {
      const body = { token, consent };
      if (ref) body.ref = ref;
      // Отметки стоят — передаём их редакции; без отметок акцепта нет.
      if (consent) body.legal = { offer: OFFER_VERSION, consent: CONSENT_VERSION };
      const response = await api.post('/login-link/redeem', body);
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 428) {
        // Новая учётная запись: bit-auth ждёт отметок «оферта» и «согласие».
        // Ссылка при этом не сгорает — повтор с consent=true пройдёт ею же.
        const need = new Error('Нужно согласие с офертой и на обработку данных.');
        need.code = 'consent_required';
        throw need;
      }
      if (status === 410) throw new Error('Ссылка устарела или уже использована.');
      if ((status === 403 || status === 409 || status === 429) && typeof detail === 'string') {
        throw new Error(detail);
      }
      throw new Error('Не удалось войти по ссылке. Попробуйте позже.');
    }
  },

  async issueApiToken() {
    try {
      const response = await api.post('/me/api-token', {}, { headers: this.getAuthHeaders() });
      return response.data;
    } catch (error) {
      throw new Error('Не удалось выпустить токен. Попробуйте позже.');
    }
  },

  async checkToken(token) {
    try {
      const response = await api.post('/check-token', { token });
      this.saveTokenData(token, response.data);
      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          throw new Error('Токен не найден.');
        } else if (error.response.status === 403) {
          // Бэкенд отдаёт 403 для двух разных случаев (main.py): деактивированный
          // аккаунт и истёкший токен. Различаем по detail — иначе деактивированному
          // показывалось ложное «токен истёк, новый отправлен на email».
          if (error.response.data?.detail === 'Account is deactivated') {
            throw await this.deactivatedError({ token }, {});
          }
          throw new Error('Срок действия токена истек. Новый токен отправлен на ваш email.');
        }
      }
      throw new Error('Ошибка при проверке токена. Попробуйте позже.');
    }
  },

  /** Для активной KC-сессии: дёргает bit-auth /check-token с Bearer, кладёт контракт в tokenData. */
  async checkKeycloakSession() {
    const auth = { headers: { Authorization: 'Bearer ' + keycloak.token } };
    let response;
    try {
      response = await api.post('/check-token', {}, auth);
    } catch (error) {
      if (error.response?.status === 403 && error.response.data?.detail === 'Account is deactivated') {
        throw await this.deactivatedError({}, auth);
      }
      // Остальные отказы (чужой домен, учётка привязана к другой identity, сбой
      // bit-auth) человеку не объяснить, но и молча вернуть его на экран входа
      // нельзя: он логинится снова и снова, не понимая, что не так.
      // Повтор помогает только при недоступном bit-auth; отказ по существу (403, 401)
      // от повтора не пройдёт — там сразу адрес поддержки.
      const err = new Error(error.response
        ? 'Не удалось войти через AI Platform. Напишите на ' + SUPPORT_EMAIL + ' — разберёмся.'
        : 'Сервер входа сейчас недоступен. Обновите страницу через минуту; '
          + 'если не поможет, напишите на ' + SUPPORT_EMAIL + '.');
      err.cause = error;
      throw err;
    }
    localStorage.setItem('tokenData', JSON.stringify(response.data));
    return response.data;
  },

  /**
   * Отказ отключённой учётке. Если владелец ключа уволен, в ошибке едет ответ
   * /departure — по нему App показывает экран клиентского Ньютона
   * (DepartedScreen) вместо «обратитесь к администратору». Спрашиваем тем же
   * ключом, что был отвергнут: по одному адресу bit-auth не отвечает.
   * Ручка недоступна — прежний отказ, а не падение.
   */
  async deactivatedError(body, config) {
    const err = new Error('Ваша учётная запись деактивирована. Напишите на '
      + SUPPORT_EMAIL + ' — разберёмся.');
    err.departure = null;
    try {
      const { data } = await api.post('/departure', body, config);
      if (data?.dismissed) err.departure = data;
    } catch (_) {
      // не знаем — показываем прежний отказ
    }
    return err;
  },

  saveTokenData(token, data) {
    localStorage.setItem('authToken', token);
    localStorage.setItem('tokenData', JSON.stringify(data));
  },

  getToken() {
    return localStorage.getItem('authToken');
  },

  getTokenData() {
    const data = localStorage.getItem('tokenData');
    return data ? JSON.parse(data) : null;
  },

  /** Заголовки авторизации для всех сервисов. KC-сессия → Bearer, иначе легаси → token. */
  getAuthHeaders() {
    if (keycloakEnabled && keycloak && keycloak.authenticated && keycloak.token) {
      return { Authorization: 'Bearer ' + keycloak.token };
    }
    const t = this.getToken();
    return t ? { token: t } : {};
  },

  /** Продлить access-токен Keycloak заранее, best-effort: живая запись
   *  переподключается после обрыва связи, и auth-кадр нового сеанса не должен
   *  уйти с токеном, протухшим за время обрыва. Легаси-токен не протухает —
   *  продлевать нечего. Ошибку не бросает: без сети продление не пройдёт, а
   *  выход из системы из-за этого — хуже, чем отказ сервера по авторизации. */
  refreshSession() {
    // Не async намеренно: легаси-сессии нечего ждать, и переподключение не должно
    // откладываться на микрозадачу ради пустого промиса.
    if (!this.isKeycloakSession()) return undefined;
    return keycloak.updateToken(70).catch(() => {
      // сеть ещё не вернулась — попробуем перед следующей попыткой
    });
  },

  /** Заголовок для ЗАГРУЗКИ файла: она длится минуты, а access-токен живёт 300 с
   *  и проверяется сервером ПОСЛЕ приёма всего тела. Кэша нет намеренно —
   *  сервер на живом токене только сдвигает срок. */
  async getUploadHeaders() {
    if (!this.isKeycloakSession()) return this.getAuthHeaders();
    try {
      // Вкладка могла пролежать в фоне дольше жизни JWT: выпуск пойдёт под ним.
      await keycloak.updateToken(70);
      const { data } = await api.post(
        '/me/purpose-token',
        { purpose: 'upload', ttl_hours: 1 },
        { headers: this.getAuthHeaders() },
      );
      return { token: data.token };
    } catch (e) {
      // Любая ошибка, включая 400 от ещё не выкаченного bit-auth: работаем как раньше.
      return this.getAuthHeaders();
    }
  },

  isAuthenticated() {
    return (keycloakEnabled && keycloak && !!keycloak.authenticated) || !!this.getToken();
  },

  /** true только для активной Keycloak-сессии (НЕ просто keycloakEnabled — под dual-login
   * легаси-юзер тоже имеет keycloakEnabled=true, но реальный токен). Источник правды для
   * выбора Bearer vs token И для скрытия токен-секций в ЛК — чтобы они не разъезжались. */
  isKeycloakSession() {
    return !!(keycloakEnabled && keycloak && keycloak.authenticated && keycloak.token);
  },

  /**
   * Причина, по которой сессию AI Platform пришлось закрыть. logout() уводит браузер
   * на Keycloak и обратно — состояние React не доживает, поэтому текст едет через
   * sessionStorage, и экран входа показывает его один раз (20.10.2026: отключённую
   * учётку молча возвращало на вход, человек не понимал, что не так).
   */
  rememberLoginNotice(text) {
    try { sessionStorage.setItem(LOGIN_NOTICE_KEY, text); } catch (_) { /* приватный режим */ }
  },

  // Чтение и сброс раздельно: StrictMode зовёт инициализатор useState дважды, и
  // «прочитать и стереть» за один вызов отдало бы второму вызову пустую строку.
  peekLoginNotice() {
    try { return sessionStorage.getItem(LOGIN_NOTICE_KEY) || ''; } catch (_) { return ''; }
  },

  clearLoginNotice() {
    try { sessionStorage.removeItem(LOGIN_NOTICE_KEY); } catch (_) { /* приватный режим */ }
  },

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenData');
    if (keycloakEnabled && keycloak && keycloak.authenticated) {
      keycloak.logout({ redirectUri: window.location.origin });
    }
  },

  isLoggedIn() {
    return this.isAuthenticated();
  },

  async getProfile() {
    const response = await api.get('/me', {
      headers: this.getAuthHeaders()
    });
    return response.data;
  },

  // Кто зарегистрировался по моим ссылкам: { invited, items: [{ email, created_at }] }.
  // Адрес приходит уже скрытым (`i***@ext.ru`) — bit-auth чужую почту целиком не отдаёт.
  async myReferrals() {
    const response = await api.get('/me/referrals', {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  },

  // Акцепт оферты и согласия из окна «Обновились документы» (bit-auth legal.py).
  async acceptLegal(offer, consent) {
    try {
      const response = await api.post('/me/legal-accept', { offer, consent }, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        const stale = new Error('Документы обновились.');
        stale.code = 'legal_version_mismatch';
        throw stale;
      }
      throw new Error('Не удалось сохранить согласие. Попробуйте ещё раз.');
    }
  },

  async updateDisplayName(displayName) {
    const response = await api.patch('/me',
      { display_name: displayName },
      { headers: this.getAuthHeaders() }
    );
    // Имя меняют из профиля и из формы «Позвать бота»; кнопка меню в боковой
    // панели подписана им — App слушает это событие и обновляет её сразу.
    const saved = response.data?.display_name ?? displayName;
    try {
      window.dispatchEvent(new CustomEvent(DISPLAY_NAME_EVENT, { detail: saved }));
    } catch (_) { /* нет window — не страшно */ }
    return response.data;
  },

  async regenerateToken() {
    const response = await api.post('/me/regenerate-token', {}, {
      headers: { 'token': this.getToken() }
    });
    return response.data;
  },
};
